import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { CheckCircle2, Calendar, Sparkles, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TrialSuccessModalProps {
  isOpen: boolean;
  trialEndDate: string;
  credits?: number;
  onContinue: () => void;
}

export const TrialSuccessModal = ({ 
  isOpen, 
  trialEndDate,
  credits,
  onContinue 
}: TrialSuccessModalProps) => {
  const { t, i18n } = useTranslation();

  // Map i18n language to locale format
  const getLocale = () => {
    const lang = i18n.language;
    if (lang.startsWith('pt')) return 'pt-BR';
    if (lang.startsWith('en')) return 'en-US';
    return 'en-US'; // Default fallback
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[480px] border-none shadow-2xl" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center text-center space-y-8 py-6">
          {/* Icon */}
          <div className="relative">
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-2xl" />
            <div className="relative p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-full border border-green-200/50 dark:border-green-800/50">
              <CheckCircle2 className="h-16 w-16 text-green-600 dark:text-green-500" strokeWidth={1.5} />
            </div>
            <div className="absolute -top-2 -right-2">
              <Sparkles className="h-8 w-8 text-yellow-500 animate-pulse" fill="currentColor" />
            </div>
          </div>

          {/* Title & Description */}
          <div className="space-y-3">
            <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 bg-clip-text text-transparent">
              {t('auth.trial.success.title', { defaultValue: 'Trial Ativado!' })}
            </h2>
            <p className="text-muted-foreground text-base max-w-sm mx-auto leading-relaxed">
              {t('auth.trial.success.description', { 
                defaultValue: 'Seja bem-vindo! Seu período de trial foi ativado com sucesso.' 
              })}
            </p>
          </div>

          {/* Info Cards - Simplified */}
          <div className="w-full space-y-3">
            {/* Trial End Date */}
            <div className="flex items-center justify-center gap-4 px-6 py-4 bg-muted/30 rounded-2xl border border-border/50">
              <div className="p-2.5 bg-background rounded-xl">
                <Calendar className="h-5 w-5 text-foreground/70" strokeWidth={1.5} />
              </div>
              <div className="text-left flex-1">
                <p className="text-xs text-muted-foreground font-medium">
                  {t('auth.trial.success.validUntil', { defaultValue: 'Trial válido até' })}
                </p>
                <p className="text-lg font-bold text-foreground mt-0.5">
                  {formatDate(trialEndDate)}
                </p>
              </div>
            </div>

            {/* Credits (if available) */}
            {credits !== undefined && credits > 0 && (
              <div className="flex items-center justify-center gap-4 px-6 py-4 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 rounded-2xl border border-amber-200/50 dark:border-amber-800/50">
                <div className="p-2.5 bg-white dark:bg-background rounded-xl">
                  <Zap className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                </div>
                <div className="text-left flex-1">
                  <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                    {t('auth.trial.success.creditsAvailable', { defaultValue: 'Créditos disponíveis' })}
                  </p>
                  <p className="text-2xl font-bold text-amber-800 dark:text-amber-300 mt-0.5">
                    {credits.toLocaleString(getLocale())}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Message */}
          <p className="text-sm text-muted-foreground/80 max-w-xs leading-relaxed">
            {t('auth.trial.success.message', { 
              defaultValue: 'Aproveite todos os recursos do Prodgy durante o período de trial!' 
            })}
          </p>

          {/* CTA Button */}
          <Button 
            onClick={() => {
              if (typeof window !== 'undefined') {
                sessionStorage.removeItem('onboarding_profile');
                sessionStorage.removeItem('onboarding_survey');
                sessionStorage.removeItem('onboarding_organization');
                sessionStorage.removeItem('onboarding_avatar_preview');
                sessionStorage.removeItem('onboarding_logo_preview');
              }
              onContinue();
            }} 
            className="w-full h-12 text-base font-semibold gap-2 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
            size="lg"
          >
            <Sparkles className="h-5 w-5" />
            {t('auth.trial.success.startButton', { defaultValue: 'Começar a Usar!' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
